import { Button, Collapse, Grid, TextField, FormControlLabel, Checkbox } from "@mui/material";
import { Icon } from "leaflet";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, Marker, Pane, Polygon, Polyline, TileLayer, useMap, useMapEvents } from "react-leaflet";
import { ThemeProvider } from "styled-components";
import { getAlertTypeNames, getMuiTheme } from "../../../../utils/utils";
import palette, { polylineColors } from "../../../../../config/colorPalette";
import { clone, isEmpty, isNil } from "ramda";
import { ErrorText } from "./styled";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { IS_ONLINE, MAP_TYPES } from "../../../../../config/configmap";
import { styled } from "@mui/material/styles";
import ChannelInfo from "./components/channelInfo";
import MeasurementInfo from "./components/measurementInfo";
import { offsetPolygonPerSegment } from "./utils";

const EquipmentInfo = ({
  equipment,
  setEquipment,
  equipmentCollection,
  polylineDisplay,
  centroid,
  assetName,
  measures,
  setMeasures,
  measuresComputed,
  pIndexes,
  measuresAlerts,
  setMeasuresAlerts,
  thresholdsValues,
  setThresholdsValues,
  thresholdsRanges,
  setThresholdsRanges,
  objectMeasures,
  zones,
  setZones,
  infoToggle,
  setInfoToggle,
  channelAsignToggle,
  setChannelAsignToggle,
  channelNameAsignToggle,
  setChannelNameAsignToggle,
  channelDataToggle,
  setChannelDataToggle,
  channelPK,
  setChannelPK,
  equipmentInterMeasurement,
  setEquipmentInterMeasurement,
  measureToggle,
  setMeasureToggle,
  channelToggle,
  setChannelToggle,
  zoneToggle,
  setZoneToggle,
  moreSubsAllowed,
  substructureAsignToggle,
  setSubstructureAsignToggle,
  substructureDataToggle,
  setSubstructureDataToggle,
  configData,
  errors,
  setErrors,
  intl,
}) => {
  const theme = useMemo(() => getMuiTheme(), []);
  const [open, setOpen] = useState([]);
  const thresholdType = useState(getAlertTypeNames(intl));
  const [hoveredPolylineIndex, setHoveredPolylineIndex] = useState(null);
  const initializedRef = useRef(false);
  const [criticalPolygon, setCriticalPolygon] = useState([]);
  const [protectionPolygon, setProtectionPolygon] = useState([]);

  const [inclinometers, setInclinometers] = useState([]);

  const handleToggle = (index) => {
    let newOpen = clone(open);
    newOpen[index] = !newOpen[index];
    for (let j = 0; j < newOpen.length; j++) {
      if (newOpen[index] && index !== j) {
        newOpen[j] = false;
      }
    }
    setOpen(newOpen);
  };

  const useStyles = {
    colorSample: {
      width: "20px",
      height: "20px",
      display: "inline-block",
      marginRight: "8px",
      border: "1px solid #ccc",
      borderRadius: "50%",
    },
  };
  const BootstrapButton = styled(Button)({
    marginBottom: "5%",
    border: "1px solid",
    lineHeight: 1.5,
    backgroundColor: palette.primary,
    borderColor: palette.secondary,
    "&:hover": {
      backgroundColor: palette.link,
      borderColor: palette.secondary,
      boxShadow: "none",
    },
    "&:active": {
      boxShadow: "none",
      backgroundColor: palette.link,
      borderColor: palette.secondary,
    },
    "&:focus": {
      boxShadow: "0 0 0 0.2rem rgba(134,26,33,.5)",
    },
  });

  const customCentroidIcon = new Icon({
    iconUrl: require("../../../../../images/Uptech-marker-icon.png"),
    iconSize: [50, 50],
    iconAnchor: [25, 50],
  });
  const customInclinometerIcon = new Icon({
    iconUrl: require("../../../../../images/Uptech-marker-icon-inclinometer.png"),
    iconSize: [50, 50],
    iconAnchor: [25, 50],
  });
  const customIcon = new Icon({
    iconUrl: require("../../../../../images/Uptech-equipment-marker-icon.png"),
    iconSize: [50, 50],
    iconAnchor: [25, 50],
  });

  const customShadowIcon = new Icon({
    iconUrl: require("../../../../../images/Uptech-equipment-marker-icon.png"),
    iconSize: [50, 50],
    iconAnchor: [25, 50],
    shadowUrl: require("../../../../../images/yellow-shadow.png"), // Ajusta la ruta de la sombra según tu estructura de archivos
    shadowSize: [150, 100], // Tamaño de la sombra, ajusta según sea necesario
    shadowAnchor: [74, 75], // Anclaje de la sombra, ajusta según sea necesario
  });

  const [mapType] = useState("regular");

  const AddEquipment = () => {
    // eslint-disable-next-line no-unused-vars
    const map = useMapEvents({
      click: (e) => {
        if (
          equipment.length < equipmentCollection.length &&
          equipmentCollection[0] !== "" &&
          !isEmpty(polylineDisplay)
        ) {
          setInfoToggle((prev) => [...prev, true]);
          setChannelAsignToggle((prev) => [...prev, false]);
          setChannelNameAsignToggle((prev) => [...prev, false]);
          setMeasureToggle((prev) => [...prev, false]);
          setChannelDataToggle((prev) => [...prev, [false]]);
          setChannelPK((prev) => [
            ...prev,
            [Math.round(polylineDisplay[0].points[polylineDisplay[0].points.length - 1].pk * 100) / 100],
          ]);
          setEquipmentInterMeasurement((prev) => [...prev, 0]);
          setSubstructureAsignToggle((prev) => [...prev, [false]]);
          let newChannelToggle = clone(channelToggle);
          let newSubToggle = clone(substructureDataToggle);
          let newZoneToggle = clone(zoneToggle);
          for (let indexEquipment = 0; indexEquipment < Object.keys(measures).length; indexEquipment++) {
            if (isNil(newZoneToggle[indexEquipment]) && isNil(newChannelToggle[indexEquipment])) {
              newChannelToggle.push([]);
              newSubToggle.push([]);
              newZoneToggle.push([]);
              for (
                let indexMeasure = 0;
                indexMeasure < measures[`equipment${indexEquipment + 1}`].length;
                indexMeasure++
              ) {
                newChannelToggle[indexEquipment].push([false]);
                newSubToggle[indexEquipment].push([]);
                newSubToggle[indexEquipment][0].push(false);
                //newChannelToggle[indexEquipment][indexMeasure].push(false);
                newZoneToggle[indexEquipment].push([]);
                newZoneToggle[indexEquipment][0].push([]);
                newZoneToggle[indexEquipment][0][0].push(false);
                //newZoneToggle[indexEquipment][indexMeasure].push([]);
                //newZoneToggle[indexEquipment][indexMeasure][0].push(false);
              }
            }
          }
          setChannelToggle(newChannelToggle);
          setSubstructureDataToggle(newSubToggle);
          setZoneToggle(newZoneToggle);
          setEquipment((prevEquipment) => [
            ...prevEquipment,
            {
              equipmentID: ``,
              name: ``,
              latitude: e.latlng.lat,
              longitude: e.latlng.lng,
              measurementInfo: {
                interval: 0,
                state: false,
              },
              moreInfo: {
                model: "",
                manufacturer: ``,
                ipAddress: "0.0.0.0",
              },
              statusInfo: {
                supportNotification: false,
                details: "OK",
              },
              asset: assetName,
              equipmentType: getRemainingEquipmentType(prevEquipment, equipmentCollection),
              spatialResolution: getRemainingEquipmentType(prevEquipment, equipmentCollection) === "DAS" ? 0 : null,
              criticalArea:
                getRemainingEquipmentType(prevEquipment, equipmentCollection) === "DAS"
                  ? { distance: 0, polygon: null }
                  : null,
              protectionArea:
                getRemainingEquipmentType(prevEquipment, equipmentCollection) === "DAS"
                  ? { distance: 0, polygon: null }
                  : null,
              channels: [
                {
                  name: "",
                  info: {
                    desc: "",
                    length: Math.round(polylineDisplay[0].points[polylineDisplay[0].points.length - 1].pk * 100) / 100,
                    samplingInterval: 0,
                    spatialResolution:
                      getRemainingEquipmentType(prevEquipment, equipmentCollection) === "DAS" ? null : 0,
                    acquisitionFrequency:
                      getRemainingEquipmentType(prevEquipment, equipmentCollection) === "DAS" ? 0 : null,
                    gaugeLength: getRemainingEquipmentType(prevEquipment, equipmentCollection) === "DAS" ? 0 : null,
                  },
                  measurement: {
                    measurementTime: getRemainingEquipmentType(prevEquipment, equipmentCollection) === "DAS" ? null : 0,
                    state: false,
                  },
                  substructures: moreSubsAllowed
                    ? [{ name: "", type: "inclinometer", polyline: [0, 0], depth: 50, sections: [""], zones: [] }]
                    : [{ name: "", type: "generic", polyline: 0, depth: null, sections: null, zones: [] }],
                },
              ],
            },
          ]);
        }
      },
    });
    return null;
  };

  const getRemainingEquipmentType = (equipment, equipmentCollection) => {
    const usedEquipmentTypes = equipment.map((item) => item.equipmentType);
    const remainingEquipmentType = equipmentCollection.find((type) => !usedEquipmentTypes.includes(type));
    return remainingEquipmentType || equipmentCollection[0];
  };

  useEffect(() => {
    const inclinometerList = [];
    if (moreSubsAllowed) {
      for (let indexEquipment = 0; indexEquipment < equipment.length; indexEquipment++) {
        for (let channelIndex = 0; channelIndex < equipment[indexEquipment].channels.length; channelIndex++) {
          for (
            let subIndex = 0;
            subIndex < equipment[indexEquipment].channels[channelIndex].substructures.length;
            subIndex++
          ) {
            if (equipment[indexEquipment].channels[channelIndex].substructures[subIndex].type === "inclinometer") {
              inclinometerList.push(equipment[indexEquipment].channels[channelIndex].substructures[subIndex].polyline);
            }
          }
        }
      }
    }

    setInclinometers(inclinometerList);
  }, [equipment, moreSubsAllowed]);

  const MyComponentSetView = ({ polyline }) => {
    const map = useMap();
    if (!initializedRef.current && !isEmpty(polyline) && !isNil(polyline[0].points) && !isEmpty(polyline[0].points)) {
      map.fitBounds(polyline[0].points);
      initializedRef.current = true;
    }
    return null;
  };

  return (
    <ThemeProvider theme={theme}>
      <div>
        <Grid Grid container direction="row" justifyContent="flex-start" alignItems="center">
          <Grid item style={{ flex: "1", marginRight: "1%" }}>
            {(isEmpty(polylineDisplay) || equipmentCollection[0] === "") && (
              <h3>{intl.formatMessage({ id: "first.polyline.and.equipment.to.equipment" })}</h3>
            )}
            <section
              style={{
                position: "relative",
                width: "100%",
                borderRadius: "4px",
                border: `1px solid ${palette.primary}`,
                margin: "1.5rem 0",
                padding: "1%",
              }}
            >
              <h1 style={{ marginBottom: "1%" }}>Equipments</h1>
              {!isNil(equipment) &&
                !isEmpty(equipment) &&
                equipment.map((e, indexEquipment) => {
                  return (
                    <div>
                      <Button id={`equipmetnCollapse${indexEquipment}`} onClick={() => handleToggle(indexEquipment)}>
                        {e.equipmentID} - {e.equipmentType}
                      </Button>
                      <DeleteForeverIcon
                        onClick={() => {
                          const newEquipments = clone(equipment);
                          newEquipments.splice(indexEquipment, 1);
                          setEquipment(newEquipments);
                        }}
                        sx={{ color: palette.primary, cursor: "pointer" }}
                      />
                      <Collapse in={open[indexEquipment]}>
                        <div>
                          <h1
                            onClick={() => {
                              let newInfoToggle = clone(infoToggle);
                              if (newInfoToggle[indexEquipment]) {
                                newInfoToggle[indexEquipment] = false;
                              } else {
                                newInfoToggle[indexEquipment] = true;
                              }
                              setInfoToggle(newInfoToggle);
                            }}
                            style={{ marginBottom: "3%" }}
                          >
                            {intl.formatMessage({ id: "equipment.info" })}
                            {!infoToggle[indexEquipment] ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />}
                          </h1>
                          <Collapse in={infoToggle[indexEquipment]}>
                            <TextField
                              id={"equipment" + indexEquipment + "name"}
                              style={{ margin: "0 0.2rem 0.8rem" }}
                              defaultValue={e.name}
                              onChange={(e) => {
                                const newErrors = { ...errors };
                                if (isEmpty(e.target.value)) {
                                  newErrors[e.target.id] = "not.empty";
                                } else {
                                  delete newErrors[e.target.id];
                                }
                                setErrors(newErrors);

                                const newEquipments = [...equipment];
                                const updatedEquipment = { ...newEquipments[indexEquipment] };
                                updatedEquipment.name = e.target.value;
                                newEquipments[indexEquipment] = updatedEquipment;

                                setEquipment(newEquipments);
                              }}
                              error={errors[equipment + indexEquipment + "name"] ? true : false}
                              variant="outlined"
                              label={intl.formatMessage({ id: "equipment.name" })}
                              required
                            ></TextField>
                            {!isNil(errors["equipment" + indexEquipment + "name"]) && (
                              <ErrorText>
                                {intl.formatMessage({ id: errors["equipment" + indexEquipment + "name"] })}
                              </ErrorText>
                            )}
                            <TextField
                              id={"equipment" + indexEquipment + "manufacturer"}
                              style={{ margin: "0 0.2rem 0.8rem" }}
                              defaultValue={e.moreInfo.manufacturer}
                              onChange={(e) => {
                                const newErrors = { ...errors };
                                if (isEmpty(e.target.value)) {
                                  newErrors[e.target.id] = "not.empty";
                                } else {
                                  delete newErrors[e.target.id];
                                }
                                setErrors(newErrors);

                                const newEquipments = [...equipment];
                                const updatedEquipment = { ...newEquipments[indexEquipment] };
                                updatedEquipment.moreInfo.manufacturer = e.target.value;
                                newEquipments[indexEquipment] = updatedEquipment;

                                setEquipment(newEquipments);
                              }}
                              error={errors[equipment + indexEquipment + "manufacturer"] ? true : false}
                              variant="outlined"
                              label={intl.formatMessage({ id: "equipment.manufacturer" })}
                              required
                            ></TextField>
                            {!isNil(errors["equipment" + indexEquipment + "manufacturer"]) && (
                              <ErrorText>
                                {intl.formatMessage({ id: errors["equipment" + indexEquipment + "manufacturer"] })}
                              </ErrorText>
                            )}
                            <TextField
                              id={"equipment" + indexEquipment + "model"}
                              style={{ margin: "0 0.2rem 0.8rem" }}
                              defaultValue={e.moreInfo.model}
                              onChange={(e) => {
                                const newErrors = { ...errors };
                                if (isEmpty(e.target.value)) {
                                  newErrors[e.target.id] = "not.empty";
                                } else {
                                  delete newErrors[e.target.id];
                                }
                                setErrors(newErrors);

                                const newEquipments = [...equipment];
                                const updatedEquipment = { ...newEquipments[indexEquipment] };
                                updatedEquipment.moreInfo.model = e.target.value;
                                newEquipments[indexEquipment] = updatedEquipment;

                                setEquipment(newEquipments);
                              }}
                              error={errors[equipment + indexEquipment + "model"] ? true : false}
                              variant="outlined"
                              label={intl.formatMessage({ id: "equipment.model" })}
                              required
                            ></TextField>
                            {!isNil(errors["equipment" + indexEquipment + "model"]) && (
                              <ErrorText>
                                {intl.formatMessage({ id: errors["equipment" + indexEquipment + "model"] })}
                              </ErrorText>
                            )}
                            <TextField
                              id={"equipment" + indexEquipment + "equipmentID"}
                              style={{ margin: "0 0.2rem 0.8rem" }}
                              defaultValue={e.equipmentID}
                              onChange={(e) => {
                                const newErrors = { ...errors };
                                if (isEmpty(e.target.value)) {
                                  newErrors[e.target.id] = "not.empty";
                                } else {
                                  delete newErrors[e.target.id];
                                }
                                setErrors(newErrors);

                                const newEquipments = [...equipment];
                                const updatedEquipment = { ...newEquipments[indexEquipment] };
                                updatedEquipment.equipmentID = e.target.value;
                                newEquipments[indexEquipment] = updatedEquipment;

                                setEquipment(newEquipments);
                              }}
                              error={errors[equipment + indexEquipment + "equipmentID"] ? true : false}
                              variant="outlined"
                              label={intl.formatMessage({ id: "equipment.equipmentID" })}
                              required
                            ></TextField>
                            {!isNil(errors["equipment" + indexEquipment + "equipmentID"]) && (
                              <ErrorText>
                                {intl.formatMessage({ id: errors["equipment" + indexEquipment + "equipmentID"] })}
                              </ErrorText>
                            )}
                            <TextField
                              id={"equipment" + indexEquipment + "_IP"}
                              style={{ margin: "0 0.2rem 0.8rem" }}
                              defaultValue={e.moreInfo.ipAddress}
                              onChange={(e) => {
                                const newErrors = { ...errors };
                                const regex =
                                  /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
                                const isValidInput = regex.test(e.target.value);
                                if (isEmpty(e.target.value)) {
                                  newErrors[e.target.id] = "not.empty";
                                } else if (!isValidInput) {
                                  newErrors[e.target.id] = "must.ip";
                                } else {
                                  delete newErrors[e.target.id];
                                }
                                setErrors(newErrors);

                                const newEquipments = [...equipment];
                                const updatedEquipment = { ...newEquipments[indexEquipment] };
                                updatedEquipment.moreInfo.ipAddress = e.target.value;
                                newEquipments[indexEquipment] = updatedEquipment;

                                setEquipment(newEquipments);
                              }}
                              error={errors[equipment + indexEquipment + "_IP"] ? true : false}
                              variant="outlined"
                              label={intl.formatMessage({ id: "ip.address" })}
                              required
                            ></TextField>
                            {!isNil(errors["equipment" + indexEquipment + "_IP"]) && (
                              <ErrorText>
                                {intl.formatMessage({ id: errors["equipment" + indexEquipment + "_IP"] })}
                              </ErrorText>
                            )}
                            <TextField
                              id={"equipment" + indexEquipment + "_channels"}
                              style={{ margin: "0 0.2rem 0.8rem" }}
                              defaultValue={e.channels.length}
                              inputProps={{ min: 1 }}
                              onChange={(e) => {
                                const newErrors = { ...errors };
                                if (isEmpty(e.target.value)) {
                                  newErrors[e.target.id] = "not.empty";
                                } else {
                                  delete newErrors[e.target.id];
                                }
                                setErrors(newErrors);

                                const newEquipments = [...equipment];
                                const updatedEquipment = { ...newEquipments[indexEquipment] };

                                let newThresholdValues = clone(thresholdsValues);
                                let newThresholdRanges = clone(thresholdsRanges);
                                let newZones = clone(zones);
                                let newChannelToggle = clone(channelToggle);
                                let newSubToggle = clone(substructureDataToggle);
                                let newZoneToggle = clone(zoneToggle);

                                updatedEquipment.channels = [];
                                let newChannelDataToggle = clone(channelDataToggle);
                                newChannelDataToggle[indexEquipment] = [];
                                newSubToggle[indexEquipment] = [];
                                let newChannelPk = clone(channelPK);
                                for (let newIndex = 0; newIndex < parseInt(e.target.value); newIndex++) {
                                  newChannelDataToggle[indexEquipment].push(false);
                                  newSubToggle[indexEquipment].push([]);
                                  newSubToggle[indexEquipment][newIndex].push(false);
                                  if (isNil(newChannelPk[indexEquipment][newIndex])) {
                                    newChannelPk[indexEquipment].push(
                                      Math.round(
                                        polylineDisplay[0].points[polylineDisplay[0].points.length - 1].pk * 100,
                                      ) / 100,
                                    );
                                  }
                                  newChannelToggle[indexEquipment].push(false);
                                  if (isNil(newZones[`equipment${indexEquipment + 1}`][`channel${newIndex + 1}`])) {
                                    newZones[`equipment${indexEquipment + 1}`][`channel${newIndex + 1}`] = {
                                      sub1: [{ ini: 0, end: 1, alertsConfigurations: null }],
                                    };
                                    newZoneToggle[indexEquipment].push([]);
                                    newZoneToggle[indexEquipment][newIndex].push([]);
                                    newZoneToggle[indexEquipment][newIndex][0].push([]);
                                  }
                                  updatedEquipment.channels.push({
                                    name: "",
                                    info: {
                                      desc: "",
                                      length:
                                        Math.round(
                                          polylineDisplay[0].points[polylineDisplay[0].points.length - 1].pk * 100,
                                        ) / 100,
                                      samplingInterval: 0,
                                      spatialResolution:
                                        getRemainingEquipmentType(equipment, equipmentCollection) === "DAS" ? null : 0,
                                      acquisitionFrequency:
                                        getRemainingEquipmentType(equipment, equipmentCollection) === "DAS" ? 0 : null,
                                      gaugeLength:
                                        getRemainingEquipmentType(equipment, equipmentCollection) === "DAS" ? 0 : null,
                                    },
                                    measurement: {
                                      measurementTime:
                                        getRemainingEquipmentType(equipment, equipmentCollection) === "DAS" ? null : 0,
                                      state: false,
                                    },
                                    substructures: moreSubsAllowed
                                      ? [
                                          {
                                            name: "",
                                            type: "inclinometer",
                                            polyline: [0, 0],
                                            depth: 50,
                                            sections: [""],
                                            zones: [],
                                          },
                                        ]
                                      : [
                                          {
                                            name: "",
                                            type: "generic",
                                            polyline: 0,
                                            depth: null,
                                            sections: null,
                                            zones: [],
                                          },
                                        ],
                                  });
                                }

                                //updatedEquipment.channels = parseInt(e.target.value);
                                newEquipments[indexEquipment] = updatedEquipment;
                                setSubstructureDataToggle(newSubToggle);
                                setChannelPK(newChannelPk);
                                setThresholdsValues(newThresholdValues);
                                setThresholdsRanges(newThresholdRanges);
                                setZones(newZones);
                                setEquipment(newEquipments);
                                setChannelToggle(newChannelToggle);
                                setZoneToggle(newZoneToggle);
                                setChannelDataToggle(newChannelDataToggle);
                              }}
                              type="number"
                              error={errors[equipment + indexEquipment + "_channels"] ? true : false}
                              variant="outlined"
                              label={intl.formatMessage({ id: "measure.channels" })}
                              required
                            ></TextField>
                            {!isNil(errors[equipment + indexEquipment + "_channels"]) && (
                              <ErrorText>
                                {intl.formatMessage({ id: errors[equipment + indexEquipment + "_channels"] })}
                              </ErrorText>
                            )}
                            {e.equipmentType === "DAS" && (
                              <>
                                <TextField
                                  id={`channel_spatial_resolution_${indexEquipment}`}
                                  style={{ margin: "0 0.2rem 0.8rem" }}
                                  inputProps={{ min: 0 }}
                                  defaultValue={0}
                                  onChange={(e) => {
                                    const newEquipments = [...equipment];
                                    const updatedEquipment = { ...newEquipments[indexEquipment] };
                                    updatedEquipment.spatialResolution = parseFloat(e.target.value);
                                    newEquipments[indexEquipment] = updatedEquipment;
                                    setEquipment(newEquipments);
                                  }}
                                  error={errors[`channel_spatial_resolution_${indexEquipment}`] ? true : false}
                                  variant="outlined"
                                  label={intl.formatMessage({ id: "channel.spatial.resolution" })}
                                  required
                                  type="number"
                                ></TextField>
                                <TextField
                                  id={`channel_critical_area_${indexEquipment}`}
                                  style={{ margin: "0 0.2rem 0.8rem" }}
                                  inputProps={{ min: 0 }}
                                  defaultValue={0}
                                  onChange={(e) => {
                                    const newEquipments = [...equipment];
                                    const updatedEquipment = { ...newEquipments[indexEquipment] };
                                    updatedEquipment.criticalArea.distance = parseFloat(e.target.value);
                                    newEquipments[indexEquipment] = updatedEquipment;
                                    setEquipment(newEquipments);
                                  }}
                                  error={errors[`channel_critical_area_${indexEquipment}`] ? true : false}
                                  variant="outlined"
                                  label={intl.formatMessage({ id: "critical.area" })}
                                  required
                                  type="number"
                                ></TextField>
                                <TextField
                                  id={`channel_protection_area_${indexEquipment}`}
                                  style={{ margin: "0 0.2rem 0.8rem" }}
                                  inputProps={{ min: 0 }}
                                  defaultValue={0}
                                  onChange={(e) => {
                                    const newEquipments = [...equipment];
                                    const updatedEquipment = { ...newEquipments[indexEquipment] };
                                    updatedEquipment.protectionArea.distance = parseFloat(e.target.value);
                                    newEquipments[indexEquipment] = updatedEquipment;
                                    setEquipment(newEquipments);
                                  }}
                                  error={errors[`channel_protection_area_${indexEquipment}`] ? true : false}
                                  variant="outlined"
                                  label={intl.formatMessage({ id: "protection.area" })}
                                  required
                                  type="number"
                                ></TextField>
                                <Button
                                  variant="contained"
                                  id="area_button"
                                  style={{
                                    backgroundColor: palette.primary,
                                    color: "white",
                                    margin: "0 0.2rem 0.8rem",
                                    height: "10.5%",
                                  }}
                                  onClick={async () => {
                                    const newEquipments = [...equipment];
                                    const updatedEquipment = { ...newEquipments[indexEquipment] };
                                    const criticalArea = updatedEquipment.criticalArea.distance;
                                    const protectionArea = updatedEquipment.protectionArea.distance;

                                    const critPolies = [];
                                    const protPolies = [];

                                    for (
                                      let indexPolyline = 0;
                                      indexPolyline < polylineDisplay.length;
                                      indexPolyline++
                                    ) {
                                      const polys = await offsetPolygonPerSegment(
                                        polylineDisplay[indexPolyline],
                                        criticalArea,
                                        protectionArea,
                                        updatedEquipment,
                                        intl,
                                      );
                                      critPolies.push(polys.criticalCoords);
                                      protPolies.push(polys.protectCoords);
                                    }
                                    setCriticalPolygon(critPolies);
                                    setProtectionPolygon(protPolies);
                                    updatedEquipment.criticalArea.polygon = critPolies;
                                    updatedEquipment.protectionArea.polygon = protPolies;
                                    newEquipments[indexEquipment] = updatedEquipment;
                                    setEquipment(newEquipments);
                                  }}
                                >
                                  {intl.formatMessage({ id: "calculate.area" })}
                                </Button>
                              </>
                            )}
                            <br></br>

                            <ChannelInfo
                              BootstrapButton={BootstrapButton}
                              channelAsignToggle={channelAsignToggle}
                              polylineColors={polylineColors}
                              polylineDisplay={polylineDisplay}
                              setChannelAsignToggle={setChannelAsignToggle}
                              setChannelNameAsignToggle={setChannelNameAsignToggle}
                              setChannelPK={setChannelPK}
                              setThresholdsRanges={setThresholdsRanges}
                              thresholdsRanges={thresholdsRanges}
                              useStyles={useStyles}
                              channelDataToggle={channelDataToggle}
                              channelNameAsignToggle={channelNameAsignToggle}
                              channelPK={channelPK}
                              e={e}
                              equipment={equipment}
                              equipmentInterMeasurement={equipmentInterMeasurement}
                              errors={errors}
                              indexEquipment={indexEquipment}
                              setChannelDataToggle={setChannelDataToggle}
                              setEquipment={setEquipment}
                              setEquipmentInterMeasurement={setEquipmentInterMeasurement}
                              hoveredPolylineIndex={hoveredPolylineIndex}
                              setHoveredPolylineIndex={setHoveredPolylineIndex}
                              setErrors={setErrors}
                              moreSubsAllowed={moreSubsAllowed}
                              centroid={centroid}
                              customCentroidIcon={customCentroidIcon}
                              customInclinometerIcon={customInclinometerIcon}
                              customIcon={customIcon}
                              customShadowIcon={customShadowIcon}
                              inclinometers={inclinometers}
                              zoneToggle={zoneToggle}
                              setZoneToggle={setZoneToggle}
                              zones={zones}
                              setZones={setZones}
                              open={open}
                              intl={intl}
                            />

                            {!isNil(errors[equipment + indexEquipment + "_duration"]) && (
                              <ErrorText>
                                {intl.formatMessage({ id: errors[equipment + indexEquipment + "_duration"] })}
                              </ErrorText>
                            )}
                            <TextField
                              id={equipment + indexEquipment + "_frequency"}
                              style={{ margin: "0 0.2rem 0.8rem", width: "50%" }}
                              defaultValue={e.measurementInfo.interval}
                              value={e.measurementInfo.interval}
                              inputProps={{ max: 86400, min: 0 }}
                              onChange={(e) => {
                                const newErrors = { ...errors };
                                if (isEmpty(e.target.value) || isNaN(e.target.value)) {
                                  newErrors[e.target.id] = "not.empty";
                                } else if (parseFloat(e.target.value) < equipmentInterMeasurement[indexEquipment]) {
                                  newErrors[e.target.id] = "not.higher.than.amount.measurement.time.channels";
                                } else if (parseFloat(e.target.value) > 86400) {
                                  newErrors[e.target.id] = "not.higher.than.day";
                                } else {
                                  delete newErrors[e.target.id];
                                }
                                setErrors(newErrors);

                                const newEquipments = [...equipment];
                                const updatedEquipment = { ...newEquipments[indexEquipment] };
                                updatedEquipment.measurementInfo.interval = parseFloat(e.target.value);
                                newEquipments[indexEquipment] = updatedEquipment;

                                setEquipment(newEquipments);
                              }}
                              type="number"
                              error={errors[equipment + indexEquipment + "_frequency"] ? true : false}
                              variant="outlined"
                              label={intl.formatMessage({ id: "measure.frequency" })}
                              required
                            ></TextField>
                            {!isNil(errors[equipment + indexEquipment + "_frequency"]) && (
                              <ErrorText>
                                {intl.formatMessage({ id: errors[equipment + indexEquipment + "_frequency"] })}
                              </ErrorText>
                            )}
                            <FormControlLabel
                              control={
                                <Checkbox
                                  id={"checkbox" + indexEquipment}
                                  sx={{
                                    color: palette.primary,
                                    "&.Mui-checked": {
                                      color: palette.primary,
                                    },
                                  }}
                                  defaultChecked={e.measurementInfo.state}
                                  onChange={(e) => {
                                    const newErrors = { ...errors };
                                    if (isEmpty(e.target.value)) {
                                      newErrors[e.target.id] = "not.empty";
                                    } else {
                                      delete newErrors[e.target.id];
                                    }
                                    setErrors(newErrors);

                                    const newEquipments = [...equipment];
                                    const updatedEquipment = { ...newEquipments[indexEquipment] };
                                    updatedEquipment.measurementInfo.state = e.target.checked;
                                    newEquipments[indexEquipment] = updatedEquipment;

                                    setEquipment(newEquipments);
                                  }}
                                />
                              }
                              label={intl.formatMessage({ id: "measurement.state" })}
                            />
                          </Collapse>
                        </div>
                        <h1
                          onClick={() => {
                            let newMeasureToggle = clone(measureToggle);
                            if (newMeasureToggle[indexEquipment]) {
                              newMeasureToggle[indexEquipment] = false;
                            } else {
                              newMeasureToggle[indexEquipment] = true;
                            }
                            setMeasureToggle(newMeasureToggle);
                          }}
                          style={{ marginBottom: "3%" }}
                        >
                          {intl.formatMessage({ id: "measure.information" })}
                          {!measureToggle[indexEquipment] ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />}
                        </h1>

                        <MeasurementInfo
                          e={e}
                          indexEquipment={indexEquipment}
                          //indexMeasure={indexMeasure}
                          //measure={measure}
                          measureToggle={measureToggle}
                          measures={measures}
                          channelToggle={channelToggle}
                          equipment={equipment}
                          errors={errors}
                          setErrors={setErrors}
                          intl={intl}
                          setThresholdsValues={setThresholdsValues}
                          setZoneToggle={setZoneToggle}
                          setZones={setZones}
                          thresholdType={thresholdType}
                          thresholdsRanges={thresholdsRanges}
                          thresholdsValues={thresholdsValues}
                          zoneToggle={zoneToggle}
                          zones={zones}
                          measuresAlerts={measuresAlerts}
                          measuresComputed={measuresComputed}
                          polylineDisplay={polylineDisplay}
                          setChannelToggle={setChannelToggle}
                          setMeasures={setMeasures}
                          setMeasuresAlerts={setMeasuresAlerts}
                          setThresholdsRanges={setThresholdsRanges}
                          configData={configData}
                          moreSubsAllowed={moreSubsAllowed}
                          setSubstructureDataToggle={setSubstructureDataToggle}
                          substructureDataToggle={substructureDataToggle}
                          key={`equipmentZoneManagement${indexEquipment}`}
                        />

                        {/*!measuresComputed[`equipment${indexEquipment + 1}`].every((elemento) =>
                          measures[`equipment${indexEquipment + 1}`].includes(elemento),
                        ) &&
                        !isNil(objectMeasures.find((obj) => obj.name === e.equipmentType).measures) &&
                        !isNil(objectMeasures.find((obj) => obj.name === e.equipmentType).computing) &&
                        measures[`equipment${indexEquipment + 1}`].length <
                          objectMeasures.find((obj) => obj.name === e.equipmentType).measures.length +
                            objectMeasures.find((obj) => obj.name === e.equipmentType).computing.length ? (
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <IconButton
                              style={{ backgroundColor: palette.dividerLight }}
                              key="add_magnitude"
                              onClick={() => {
                                //setTuples([...tuples, { type: null, value: null }]);
                                const localMeasures = clone(measures);
                                const localMeasuresAlerts = clone(measuresAlerts);
                                const localThresholdsValues = clone(thresholdsValues);
                                localMeasures[`equipment${indexEquipment + 1}`].push("");
                                localMeasuresAlerts[`equipment${indexEquipment + 1}`].push(0);
                                localThresholdsValues[`equipment${indexEquipment + 1}`].push([
                                  [[{ type: null, value: 0.5 }]],
                                ]);
                                setMeasures(localMeasures);
                                setMeasuresAlerts(localMeasuresAlerts);
                                setThresholdsValues(localThresholdsValues);
                              }}
                            >
                              <AddIcon sx={{ color: palette.primary }} />
                            </IconButton>
                            <div
                              style={{
                                width: "100%",
                                height: "1px",
                                backgroundColor: palette.primary,
                                margin: "8px 0",
                              }}
                            ></div>
                          </div>
                        ) : null*/}
                      </Collapse>
                    </div>
                  );
                })}
            </section>
          </Grid>
          <Grid item style={{ flex: "2" }}>
            <MapContainer
              id="map-equipment-id"
              center={[43, 0]}
              zoom={5}
              style={{ height: "calc(100vh - 320px)", width: "60vw" }}
              anima
            >
              <TileLayer
                attribution={IS_ONLINE ? "Google Maps" : "OpenStreetMap"}
                url={MAP_TYPES.find((type) => type.value === mapType).tileLayer}
                maxZoom={18}
                subdomains={["mt0", "mt1", "mt2", "mt3"]}
              />
              <Pane name="polygonsPane" style={{ zIndex: 400 }} />
              <Pane name="linesPane" style={{ zIndex: 500 }} />
              {centroid.length > 0 && <Marker key={"Centroid"} position={centroid} icon={customCentroidIcon}></Marker>}
              <MyComponentSetView polyline={polylineDisplay} />
              {isNil(hoveredPolylineIndex) &&
                polylineDisplay.length > 0 &&
                // eslint-disable-next-line array-callback-return
                polylineDisplay.map((polyline, index) => {
                  // eslint-disable-next-line array-callback-return
                  return polyline.points.map((point, indice) => {
                    if (indice === 0 || polyline.must_graph_points.indexOf(indice) === 0) {
                      return null;
                    }

                    if (polyline.must_graph_points.includes(indice)) {
                      const previousIndex = polyline.must_graph_points[polyline.must_graph_points.indexOf(indice) - 1];
                      const center = [
                        [
                          polylineDisplay[index].points[previousIndex].lat,
                          polylineDisplay[index].points[previousIndex].lng,
                        ],
                        [point.lat, point.lng],
                      ];
                      let auxColor = "black";
                      for (let indexEquipment = 0; indexEquipment < equipment.length; indexEquipment++) {
                        //if (equipment[indexEquipment].channels.includes(index)) {
                        //  auxColor = "red";
                        //}
                      }
                      const color = polylineDisplay.length > 10 ? auxColor : polylineColors[index];
                      return (
                        <Polyline key={indice + "_" + index} color={color} positions={center} weight={5}></Polyline>
                      );
                    }
                  });
                })}
              {!isNil(criticalPolygon) &&
                !isEmpty(criticalPolygon) &&
                criticalPolygon.map((poly, polyIndex) => (
                  <Polygon
                    positions={poly}
                    pane="polygonsPane"
                    key={`crit${polyIndex}`}
                    pathOptions={{ color: "red" }}
                  />
                ))}
              {!isNil(protectionPolygon) &&
                !isEmpty(protectionPolygon) &&
                protectionPolygon.map((poly, polyIndex) => (
                  <Polygon
                    positions={poly}
                    pane="polygonsPane"
                    key={`prot${polyIndex}`}
                    pathOptions={{ color: "green" }}
                  />
                ))}
              {!isNil(hoveredPolylineIndex) &&
                polylineDisplay.length > 0 &&
                // eslint-disable-next-line array-callback-return
                polylineDisplay.map((polyline, index) => {
                  // eslint-disable-next-line array-callback-return
                  return polyline.points.map((point, indice) => {
                    if (indice === 0 || polyline.must_graph_points.indexOf(indice) === 0) {
                      return null;
                    }

                    if (polyline.must_graph_points.includes(indice)) {
                      const previousIndex = polyline.must_graph_points[polyline.must_graph_points.indexOf(indice) - 1];
                      const center = [
                        [
                          polylineDisplay[index].points[previousIndex].lat,
                          polylineDisplay[index].points[previousIndex].lng,
                        ],
                        [point.lat, point.lng],
                      ];
                      let auxColor = "black";
                      for (let indexEquipment = 0; indexEquipment < equipment.length; indexEquipment++) {
                        //if (equipment[indexEquipment].channels.includes(index)) {
                        //  auxColor = "red";
                        //}
                      }
                      if (index === hoveredPolylineIndex) {
                        auxColor = "#0dff00";
                      }
                      const color = auxColor;
                      return (
                        <Polyline key={indice + "_" + index} color={color} positions={center} weight={5}></Polyline>
                      );
                    }
                  });
                })}
              <AddEquipment />
              {equipment.length > 0 &&
                equipment.map((equip, index) => (
                  <Marker
                    key={"equipment" + index}
                    position={[equip.latitude, equip.longitude]}
                    icon={open[index] ? customShadowIcon : customIcon}
                  ></Marker>
                ))}
            </MapContainer>
          </Grid>
        </Grid>
      </div>
    </ThemeProvider>
  );
};
export default EquipmentInfo;
